import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import { useBrand } from "../context/BrandContext";
import { useAuth } from "../context/AuthContext";
import { api, errMessage } from "../lib/api";

const STEPS = ["Child Setup", "Goal Setter", "Safety Shield"];
const AGE_BANDS = [
  { value: "2-4", label: "2–4 years", hint: "First words & sounds" },
  { value: "5-7", label: "5–7 years", hint: "Sentences & stories" },
  { value: "8-12", label: "8–12 years", hint: "Conversation & confidence" },
];
const AVATAR_COLORS = [
  { key: "amber", cls: "bg-amber-400" },
  { key: "sky", cls: "bg-sky-400" },
  { key: "emerald", cls: "bg-emerald-400" },
  { key: "rose", cls: "bg-rose-400" },
  { key: "violet", cls: "bg-violet-400" },
];
const INTERESTS = ["Animals", "Music", "Space", "Dinosaurs", "Vehicles", "Art", "Sports", "Nature", "Stories", "Numbers"];
const GOALS = ["Learn new words", "Speak in sentences", "Take turns talking", "Tell stories", "Ask questions", "Practice sounds"];
const ACCESSIBILITY_OPTIONS = [
  { key: "reduced_motion", label: "Reduce motion", hint: "Calmer screens with fewer animations" },
  { key: "captions", label: "Always show captions", hint: "Text shown for everything the coach says" },
  { key: "larger_text", label: "Larger text", hint: "Bigger letters throughout child mode" },
  { key: "audio_prompts", label: "Read-aloud in stories", hint: "A 'Read to me' button that uses your device's built-in voice" },
];

export default function Onboarding() {
  const brand = useBrand();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [step, setStep] = useState(0);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [purposes, setPurposes] = useState({
    core_learning: true,
    progress_tracking: true,
    accessibility_personalization: true,
    ai_story_generation: false,
    ai_companion_chat: false,
  });
  const [showAdjust, setShowAdjust] = useState(false);
  const [profile, setProfile] = useState({ display_name: "", age_band: "", avatar_color: "amber" });
  const [interests, setInterests] = useState([]);
  const [goals, setGoals] = useState([]);
  const [accessibility, setAccessibility] = useState({
    reduced_motion: false, captions: true, larger_text: false, audio_prompts: false,
  });
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);

  const joinFamily = async (e) => {
    e.preventDefault();
    setJoining(true);
    try {
      await api.post("/family/redeem", { code: joinCode.trim() });
      await checkAuth();
      toast.success("Welcome to the family!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setJoining(false);
    }
  };

  useEffect(() => {
    api.get("/consent/notice").then(({ data }) => setNotice(data)).catch(() => {});
  }, []);

  const toggleIn = (list, setList, item) =>
    setList(list.includes(item) ? list.filter((i) => i !== item) : [...list, item]);

  const canContinue = () => {
    if (step === 0) return profile.display_name.trim() && profile.age_band;
    return true;
  };

  const submit = async () => {
    if (!notice) { toast.error("Privacy notice is still loading — one moment"); return; }
    setSaving(true);
    setSubmitError(null);
    try {
      await api.post("/children", {
        display_name: profile.display_name.trim(),
        age_band: profile.age_band,
        interests,
        goals,
        accessibility,
        avatar_color: profile.avatar_color,
        consent: {
          purposes,
          notice_version: notice.version,
          guardian_attestation: true,  // the Safety Shield button IS the attestation
        },
      });
      toast.success(`${profile.display_name} is ready to explore!`);
      // GA4: onboarding conversion — no child PII, band only
      import("../lib/analytics").then(({ track: t }) =>
        t("onboarding_completed", { age_band: profile.age_band }));
      await checkAuth();
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const next = () => (step === STEPS.length - 1 ? submit() : setStep(step + 1));

  return (
    <div className="min-h-screen bg-background grain-surface">
      <header className="max-w-xl mx-auto px-6 py-8">
        <p className="text-sm font-bold text-secondary uppercase tracking-widest">{brand.platform_name}</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold font-display mt-2">Set up your family space</h1>
        <div className="mt-6 space-y-2">
          <Progress value={((step + 1) / STEPS.length) * 100} aria-label="Onboarding progress" data-testid="onboarding-progress" />
          <p className="text-sm text-muted-foreground">
            Step {step + 1} of {STEPS.length} — {STEPS[step]}
          </p>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-6 pb-24">
        <div className="bg-card rounded-3xl border border-border p-8 space-y-6">
          {step === 2 && (
            <div className="space-y-5" data-testid="onboarding-step-safety">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-6 w-6 text-secondary" aria-hidden="true" />
                <h2 className="text-xl font-bold font-display">Safety Shield</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                One click switches on every protection for {profile.display_name || "your child"} —
                you stay in full control and can change anything later from your dashboard.
              </p>
              <ul className="rounded-xl border border-border bg-muted/30 p-4 space-y-2">
                {["Photos & voice audio are never stored — only practice scores",
                  "No ads, no selling, no third-party sharing — ever",
                  "First-name-only everywhere; export or erase all data anytime",
                  "AI story & chat features stay OFF until you turn them on"].map((t) => (
                  <li key={t} className="text-sm flex gap-2">
                    <CheckCircle2 className="h-4 w-4 text-secondary shrink-0 mt-0.5" aria-hidden="true" /> {t}
                  </li>
                ))}
              </ul>
              <button type="button" onClick={() => setShowAdjust(!showAdjust)}
                className="text-xs font-bold underline text-muted-foreground hover:text-foreground"
                data-testid="onboarding-adjust-permissions">
                {showAdjust ? "Hide detailed permissions" : "Adjust detailed permissions"}
              </button>
              {showAdjust && notice?.purposes?.map((p) => (
                <div key={p.key} className="flex items-start justify-between gap-4 rounded-xl border border-border p-3">
                  <div>
                    <p className="font-semibold text-sm">{p.label}{p.required && <span className="ml-2 text-xs text-primary font-bold">Required</span>}</p>
                    <p className="text-xs text-muted-foreground mt-1">{p.description}</p>
                  </div>
                  <Switch
                    checked={p.required ? true : Boolean(purposes[p.key])}
                    disabled={p.required}
                    onCheckedChange={(v) => setPurposes({ ...purposes, [p.key]: Boolean(v) })}
                    aria-label={p.label}
                    data-testid={`consent-toggle-${p.key}`}
                  />
                </div>
              ))}
              <p className="text-xs text-muted-foreground rounded-xl bg-accent p-3 leading-relaxed">
                By activating the Safety Shield you confirm you are this child's parent or legal
                guardian, you are 18 or older, and you consent to the enabled purposes
                (privacy notice v{notice?.version}). Full notice:{" "}
                <Link to="/privacy" className="underline">Privacy Policy</Link>.
              </p>
            </div>
          )}

          {step === 0 && (
            <div className="space-y-6" data-testid="onboarding-step-profile">
              <h2 className="text-xl font-bold font-display">Tell us about your child</h2>
              <div className="space-y-2">
                <Label htmlFor="child-name">First name or nickname only</Label>
                <Input id="child-name" maxLength={40} value={profile.display_name}
                  onChange={(e) => setProfile({ ...profile, display_name: e.target.value })}
                  placeholder="e.g. Mia"
                  data-testid="onboarding-child-name-input" />
                <p className="text-xs text-muted-foreground">We only ask for the minimum — no birthdate, no last name.</p>
              </div>
              <div className="space-y-3">
                <Label>Age band</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup" aria-label="Age band">
                  {AGE_BANDS.map((band) => (
                    <button key={band.value} type="button" role="radio"
                      aria-checked={profile.age_band === band.value}
                      onClick={() => setProfile({ ...profile, age_band: band.value })}
                      className={`rounded-xl border-2 p-4 text-left transition-colors ${
                        profile.age_band === band.value ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                      }`}
                      data-testid={`onboarding-ageband-${band.value}`}
                    >
                      <p className="font-bold font-display">{band.label}</p>
                      <p className="text-xs text-muted-foreground mt-1">{band.hint}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <Label>Pick a color for their avatar</Label>
                <div className="flex gap-3">
                  {AVATAR_COLORS.map((c) => (
                    <button key={c.key} type="button"
                      aria-label={`Avatar color ${c.key}`}
                      onClick={() => setProfile({ ...profile, avatar_color: c.key })}
                      className={`h-12 w-12 rounded-full ${c.cls} ${
                        profile.avatar_color === c.key ? "ring-4 ring-offset-2 ring-primary" : ""
                      }`}
                      data-testid={`onboarding-avatar-${c.key}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6" data-testid="onboarding-step-interests">
              <h2 className="text-xl font-bold font-display">What do they love?</h2>
              <div className="space-y-3">
                <Label>Interests (tap all that apply)</Label>
                <div className="flex flex-wrap gap-2">
                  {INTERESTS.map((item) => (
                    <button key={item} type="button" aria-pressed={interests.includes(item)}
                      onClick={() => toggleIn(interests, setInterests, item)}
                      className={`rounded-full px-4 py-2 text-sm font-semibold border-2 transition-colors ${
                        interests.includes(item) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50"
                      }`}
                      data-testid={`onboarding-interest-${item.toLowerCase()}`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <Label>Communication goals</Label>
                <div className="flex flex-wrap gap-2">
                  {GOALS.map((item) => (
                    <button key={item} type="button" aria-pressed={goals.includes(item)}
                      onClick={() => toggleIn(goals, setGoals, item)}
                      className={`rounded-full px-4 py-2 text-sm font-semibold border-2 transition-colors ${
                        goals.includes(item) ? "border-secondary bg-secondary text-secondary-foreground" : "border-border hover:border-secondary/50"
                      }`}
                      data-testid={`onboarding-goal-${item.toLowerCase().replace(/ /g, "-")}`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5" data-testid="onboarding-step-accessibility">
              <h2 className="text-base font-bold font-display">Make it comfortable</h2>
              <p className="text-sm text-muted-foreground">These settings shape your child's experience from the very first screen.</p>
              {ACCESSIBILITY_OPTIONS.map((opt) => (
                <div key={opt.key} className="flex items-start justify-between gap-4 rounded-xl border border-border p-4">
                  <div>
                    <p className="font-semibold text-sm">{opt.label}</p>
                    <p className="text-sm text-muted-foreground mt-1">{opt.hint}</p>
                  </div>
                  <Switch checked={Boolean(accessibility[opt.key])}
                    onCheckedChange={(v) => setAccessibility({ ...accessibility, [opt.key]: Boolean(v) })}
                    aria-label={opt.label}
                    data-testid={`accessibility-toggle-${opt.key}`} />
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <Button type="button" variant="ghost" className="rounded-xl gap-2"
              onClick={() => (step === 0 ? navigate("/dashboard") : setStep(step - 1))}
              data-testid="onboarding-back-button">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {step === 0 ? "Skip for now" : "Back"}
            </Button>
            <Button type="button" className="rounded-xl gap-2 h-12 px-6" disabled={!canContinue() || saving}
              onClick={next} data-testid="onboarding-next-button">
              {step === STEPS.length - 1 ? (
                <>
                  {saving ? "Creating…" : "Activate Safety Shield & Finish"} <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                </>
              ) : (
                <>
                  Continue <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </>
              )}
            </Button>
          </div>
        </div>

        {submitError && (
          <div className="mt-4 rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm"
            role="alert" data-testid="onboarding-submit-error">
            <p className="font-bold">{submitError.message}</p>
            {submitError.upgrade && (
              <p className="mt-1">
                <Link to="/billing" className="underline font-bold" data-testid="onboarding-upgrade-link">
                  See plans to add more children
                </Link>
              </p>
            )}
          </div>
        )}

        {step === 0 && (
          <form onSubmit={joinFamily} className="mt-6 bg-card rounded-2xl border border-dashed border-border p-5 space-y-3" data-testid="onboarding-join-family">
            <p className="text-sm font-bold">Invited to join an existing family?</p>
            <p className="text-xs text-muted-foreground">
              If a parent or grandparent sent you a family invite code, enter it here — no child setup needed.
            </p>
            <div className="flex gap-2">
              <Input required placeholder="Invite code" value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)} className="h-10 text-sm" data-testid="onboarding-join-input" />
              <Button type="submit" variant="outline" disabled={joining} className="rounded-lg shrink-0 h-10" data-testid="onboarding-join-button">
                {joining ? "Joining…" : "Join family"}
              </Button>
            </div>
          </form>
        )}
        <p className="text-xs text-muted-foreground text-center pt-4">
          Your family's data is protected —{" "}
          <Link to="/privacy" className="underline" data-testid="onboarding-privacy-link">Privacy Policy</Link>{" "}
          ·{" "}
          <Link to="/terms" className="underline" data-testid="onboarding-terms-link">Terms</Link>
        </p>
      </main>
    </div>
  );
}
